import React from "react";
import { EntityChipShell, useInsidePreviewPopup, useIsTouchDevice } from "@salestrak/ui";

import { type EntityData } from "#app/lib/entity-visuals";

import { useEntityDetailOptional } from "./entity-detail-provider";
import { EntityPreview, hasEntityPreview } from "./entity-preview";
import type { Omittable } from "@salestrak/ui";

// Passes NO onClick to the shell so clicks bubble through to whatever wraps it
// (e.g. a selector trigger). MUST sit inside the trigger button, below the
// selector's PopoverTrigger — wrapping the trigger would re-parent it here.
export type EntitySelectionHoverProps = Omittable<{
  /** The one entity the wrapped value stands for. Null renders the children
   *  bare — an empty value has no preview. A GROUP of entities is
   *  `EntityGroupTag`, whose faces each carry this and whose "+N" reads the
   *  rest out in a tooltip. */
  entity: EntityData | null;
  /** Suppress the hover preview (e.g. while the selector's picker is open). */
  disabled?: boolean;
  /** Click toggles the preview. Only for standalone chips (profile dialogs,
   *  read-only cells) — never inside selector triggers, where the click must
   *  bubble through to open the picker. Without it a touch device shows no
   *  preview at all: a tap there has no hover to tell it apart from the
   *  picker's press, and would open both. */
  clickOpensPreview?: boolean;
  children: React.ReactNode;
}>;

export function EntitySelectionHover({
  entity,
  disabled,
  clickOpensPreview,
  children,
}: EntitySelectionHoverProps) {
  const detail = useEntityDetailOptional();
  const insidePreview = useInsidePreviewPopup();
  const isTouch = useIsTouchDevice();
  // A member the viewer may not open has no hover card either — the card is
  // a profile excerpt (see `canOpenEntity`).
  const profileWithheld =
    entity?.kind === "user" && detail?.canOpenEntity("user", entity.id) === false;
  if (!entity || !hasEntityPreview(entity) || profileWithheld) return <>{children}</>;

  // A preview is one layer deep: a face in a card's stack opens its page on
  // click — the same click a named chip in the card takes — never a second
  // card. With nothing to open the face is inert.
  if (insidePreview) {
    if (!clickOpensPreview || !detail?.canOpenEntity(entity.kind, entity.id)) {
      return <>{children}</>;
    }
    return (
      <EntityChipShell onClick={() => detail.openEntity(entity.kind, entity.id)}>
        {children}
      </EntityChipShell>
    );
  }

  return (
    <EntityChipShell
      disableHover={disabled || (isTouch && !clickOpensPreview)}
      clickOpensPreview={clickOpensPreview && !disabled}
      side="bottom"
      align="start"
      // Longer close grace lets the pointer travel from the clickable field
      // into the list before it dismisses.
      closeDelay={300}
      renderPreview={({ close }) => (
        <EntityPreview entity={entity} onNavigate={close} />
      )}
    >
      {children}
    </EntityChipShell>
  );
}
